import React, { useState, useEffect } from 'react';
import {
  Calculator,
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Users,
  Calendar,
  Download,
  RefreshCw,
  Filter,
  Search,
  Eye,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Coins,
  Activity,
  Target,
  Zap,
  ExternalLink,
  Wallet,
  Server,
  Gift,
  Flame,
  Lock,
  Send,
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';
import voltronWalletService, { TransactionRecord, SettlementData as WalletSettlementData } from '../../services/voltronWalletService';

interface SettlementRecord {
  id: string;
  period: string;
  totalRevenue: number;
  totalCommissions: number;
  totalBenefits: number;
  participantCount: number;
  status: 'pending' | 'processing' | 'completed';
  createdAt: string;
  completedAt?: string;
}

interface CommissionBreakdown {
  distributorAddress: string;
  distributorName: string;
  tier: string;
  stakingCommissions: number;
  nodeCommissions: number;
  totalCommissions: number;
  transactionCount: number;
}

interface SettlementStats {
  totalRevenue: number;
  totalCommissions: number;
  totalBenefits: number;
  activeDistributors: number;
  pendingSettlements: number;
}

const SettlementPage: React.FC = () => {
  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [commissionBreakdown, setCommissionBreakdown] = useState<CommissionBreakdown[]>([]);
  const [stats, setStats] = useState<SettlementStats>({
    totalRevenue: 0,
    totalCommissions: 0,
    totalBenefits: 0,
    activeDistributors: 0,
    pendingSettlements: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadSettlementData();
    const interval = setInterval(loadSettlementData, 60000);
    return () => clearInterval(interval);
  }, [selectedPeriod]);

  const loadSettlementData = async () => {
    try {
      setLoading(true);
      
      // 정산 데이터 조회
      const [settlementsRes, commissionsRes, statsRes] = await Promise.all([
        fetch(`http://localhost:3600/api/admin/settlements?period=${selectedPeriod}`),
      fetch(`http://localhost:3600/api/admin/commission-breakdown?period=${selectedPeriod}`),
      fetch('http://localhost:3600/api/admin/settlement-stats')
      ]);
      
      if (settlementsRes.ok) {
        const settlementsData = await settlementsRes.json();
        if (settlementsData.success) {
          setSettlements(settlementsData.data);
        }
      }
      
      if (commissionsRes.ok) {
        const commissionsData = await commissionsRes.json();
        if (commissionsData.success) {
          setCommissionBreakdown(commissionsData.data);
        }
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.success) {
          setStats(statsData.data);
        }
      }
      
      // Mock data for development
      const mockSettlements: SettlementRecord[] = [
        {
          id: 'settlement-2024-01',
          period: '2024-01',
          totalRevenue: 5420000,
          totalCommissions: 2168000,
          totalBenefits: 542000,
          participantCount: 1250,
          status: 'completed',
          createdAt: '2024-01-31T23:59:59Z',
          completedAt: '2024-02-01T12:00:00Z'
        },
        {
          id: 'settlement-2024-02',
          period: '2024-02',
          totalRevenue: 6890000,
          totalCommissions: 2756000,
          totalBenefits: 689000,
          participantCount: 1580,
          status: 'processing',
          createdAt: '2024-02-29T23:59:59Z'
        }
      ];
      
      const mockCommissions: CommissionBreakdown[] = [
        {
          distributorAddress: '0x1234567890123456789012345678901234111111',
          distributorName: '박진홍',
          tier: 'PLATINUM',
          stakingCommissions: 1200000,
          nodeCommissions: 800000,
          totalCommissions: 2000000,
          transactionCount: 450
        },
        {
          distributorAddress: '0x1234567890123456789012345678901234222222',
          distributorName: '박현욱',
          tier: 'DIAMOND',
          stakingCommissions: 450000,
          nodeCommissions: 300000,
          totalCommissions: 750000,
          transactionCount: 180
        }
      ];
      
      setSettlements(mockSettlements);
      setCommissionBreakdown(mockCommissions);
      
    } catch (error) {
      console.error('Failed to load settlement data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewSettlement = async () => {
    try {
      const response = await fetch('http://localhost:3600/api/admin/settlements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          period: new Date().toISOString().slice(0, 7) // YYYY-MM format
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await loadSettlementData();
          setShowCreateModal(false);
        }
      }
    } catch (error) {
      console.error('Failed to create settlement:', error);
    }
  };

  const exportSettlement = async (settlementId: string) => {
    try {
      const response = await fetch(`http://localhost:3600/api/admin/settlements/${settlementId}/export`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `settlement-${settlementId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to export settlement:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'processing': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'processing': return <Clock className="w-4 h-4" />;
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredCommissions = commissionBreakdown.filter(commission =>
    commission.distributorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    commission.distributorAddress.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settlement Management</h1>
          <p className="text-gray-600 mt-2">Commission distribution and financial settlement</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Calculator className="w-4 h-4" />
          <span>New Settlement</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalRevenue / 1000000).toFixed(1)}M</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Commissions</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalCommissions / 1000000).toFixed(1)}M</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Benefits</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalBenefits / 1000000).toFixed(1)}M</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Calculator className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Distributors</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeDistributors}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Settlements</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingSettlements}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="input"
          >
            <option value="current">Current Month</option>
            <option value="2024-02">February 2024</option>
            <option value="2024-01">January 2024</option>
            <option value="2023-12">December 2023</option>
          </select>
        </div>
      </div>

      {/* Settlements List */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Settlement History</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Benefits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settlements.map((settlement) => (
                <tr key={settlement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{settlement.period}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(settlement.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${settlement.totalRevenue.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${settlement.totalCommissions.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${settlement.totalBenefits.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{settlement.participantCount}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(settlement.status)}`}>
                      {getStatusIcon(settlement.status)}
                      <span className="ml-1 capitalize">{settlement.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => exportSettlement(settlement.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Commission Breakdown */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Commission Breakdown</h3>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search distributors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distributor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staking Commissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Node Commissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Commissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transactions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCommissions.map((commission) => (
                <tr key={commission.distributorAddress} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{commission.distributorName}</div>
                        <div className="text-sm text-gray-500">
                          {commission.distributorAddress.slice(0, 10)}...{commission.distributorAddress.slice(-8)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${
                      commission.tier === 'PLATINUM' ? 'text-purple-600 bg-purple-50 border border-purple-200' :
                      commission.tier === 'DIAMOND' ? 'text-blue-600 bg-blue-50 border border-blue-200' :
                      commission.tier === 'GOLD' ? 'text-yellow-600 bg-yellow-50 border border-yellow-200' :
                      'text-gray-600 bg-gray-50 border border-gray-200'
                    }`}>
                      {commission.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${commission.stakingCommissions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${commission.nodeCommissions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${commission.totalCommissions.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {commission.transactionCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Settlement Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Settlement</h3>
            <p className="text-gray-600 mb-6">
              This will create a new settlement for the current period and calculate all commissions and benefits.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={createNewSettlement}
                className="btn btn-primary"
              >
                Create Settlement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementPage;